import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  getCustomerBillingActivationLink,
  getCustomerBillingLink,
  getCustomerBillingUpdateLink,
  getCustomerPortalLink,
} from 'applicaiton/store/reducers/ClinicOwner/ActionCreators';
import { CreditCardIcon, VideoCameraIcon } from 'applicaiton/assets';
import { PathNames } from 'applicaiton/routes';
import {
  cancelSubscriptionPlan,
  getCurrentSubscription,
} from 'applicaiton/store/reducers/ClinicOwnerSubscription/ActionCreators';

import Loader from 'common/components/Loader';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { featuresPermissions } from 'common/helpers/featuresPermissions';

import {
  Wrapper,
  CardStyled,
  Content,
  Badges,
  StyledLink,
  ButtonWrapper,
  NameWrapper,
} from './styles';
import { UserRoles } from 'applicaiton/constants/userRoles';
import { getUserRole } from 'applicaiton/sessionStorage/auth';
import ConfirmCancelPlan from '../ConfirmCancelPlan';
import SuccessModal from '../SuccessModal';
import { resetCanceledPlan } from 'applicaiton/store/reducers/ClinicOwnerSubscription/ClinicOwnerSubscriptionSlice';

const BillingsList: FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [showConfirmCancelPlanModal, setShowConfirmCancelPlanModal] =
    useState(false);

  const { customerPortalLink, currentClinicOwner, customerBillingLink } =
    useAppSelector((state) => state.clinicOwnerSlice);
  const { isLoading, subscriptionPackage, canceledPlan } = useAppSelector(
    (state) => state.clinicOwnerSubscription,
  );
  const userRole = getUserRole();
  const isClinicOwnerRole = userRole?.includes(UserRoles.clinicOwner);
  const { hasTeleconsultationPermission } = featuresPermissions();

  const subscriptionName = subscriptionPackage
    ? subscriptionPackage.productName
    : t('clinic_billing.current_plan.no_subscription_plan');

  const subscriptionEndDate = dayjs(
    subscriptionPackage?.purchasedSubscription?.currentPeriodEnd,
  )
    .locale(i18n.language)
    .format('MMM DD, YYYY');

  const isCanceledSubscription = useMemo(() => {
    return (
      subscriptionPackage?.purchasedSubscription?.cancelAtPeriodEnd ||
      subscriptionPackage?.purchasedSubscription?.platformSubscriptionStatus ===
        'cancelled'
    );
  }, [
    subscriptionPackage?.purchasedSubscription?.cancelAtPeriodEnd,
    subscriptionPackage?.purchasedSubscription?.platformSubscriptionStatus,
  ]);

  useEffect(() => {
    if (currentClinicOwner) {
      const getLinks = async () => {
        dispatch(getCurrentSubscription());
        dispatch(getCustomerPortalLink());
        dispatch(getCustomerBillingLink(currentClinicOwner.id!)).then(
          (data) => {
            if (data.meta.requestStatus === 'fulfilled') {
              if (data.payload) {
                dispatch(getCustomerBillingUpdateLink());
              } else {
                dispatch(getCustomerBillingActivationLink());
              }
            }
          },
        );
      };

      getLinks();
    }
  }, [currentClinicOwner]);

  if (!customerPortalLink?.length || !customerBillingLink?.length) {
    return <Loader />;
  }

  const handleCancelPlan = async () => {
    dispatch(cancelSubscriptionPlan()).then((data) => {
      if (data.meta.requestStatus === 'fulfilled') {
        setShowConfirmCancelPlanModal(false);
        dispatch(getCurrentSubscription());
      }
    });
  };

  const handleResetCancelPlan = () => {
    dispatch(resetCanceledPlan());
  };

  const subscriptionEndDateText = isCanceledSubscription
    ? t('clinic_billing.current_plan.text_canceled_2')
    : t('clinic_billing.current_plan.text_2');

  return (
    <Wrapper>
      {customerPortalLink && (
        <CardStyled>
          <Content>
            <CreditCardIcon />
            <h2>{t('clinic_billing.billing_account.title')}</h2>
            <p>{t('clinic_billing.billing_account.description')}</p>
          </Content>
          <StyledLink to={customerPortalLink} target="_blank">
            {t('clinic_owner.billing.button_text')}
          </StyledLink>
        </CardStyled>
      )}
      {customerBillingLink && hasTeleconsultationPermission && (
        <CardStyled>
          <Content>
            <VideoCameraIcon />
            <h2>{t('clinic_billing.teleconsultation_payment.title')}</h2>
            <p>{t('clinic_billing.teleconsultation_payment.description')}</p>
          </Content>
          <StyledLink to={customerBillingLink} target="_blank">
            {t('clinic_billing.teleconsultation_payment.action_text')}
          </StyledLink>
        </CardStyled>
      )}
      {isClinicOwnerRole && (
        <CardStyled>
          <Content>
            <Badges>
              <CreditCardIcon />
              <p>{t('clinic_billing.current_plan.title')}</p>
            </Badges>
            <NameWrapper>
              <h2>{subscriptionName}</h2>
              {isCanceledSubscription && <span>{t('cancelled')}</span>}
            </NameWrapper>
            {subscriptionPackage?.purchasedSubscription?.currentPeriodEnd && (
              <p>
                {t('clinic_billing.current_plan.text_1')}{' '}
                {subscriptionPackage.productName} {subscriptionEndDateText}{' '}
                <b>{subscriptionEndDate}</b>
              </p>
            )}
          </Content>
          <ButtonWrapper>
            <button
              disabled={isCanceledSubscription}
              onClick={() => setShowConfirmCancelPlanModal(true)}
            >
              {t('clinic_billing.current_plan.cancel_plan')}
            </button>
            <StyledLink to={PathNames.managePlan}>
              {subscriptionPackage
                ? t('clinic_billing.current_plan.change_plan')
                : t('clinic_billing.current_plan.select_plan')}
            </StyledLink>
          </ButtonWrapper>
        </CardStyled>
      )}
      <ConfirmCancelPlan
        onClose={() => setShowConfirmCancelPlanModal(false)}
        onSubmit={handleCancelPlan}
        isOpen={showConfirmCancelPlanModal}
        planName={subscriptionName}
        isLoading={isLoading}
      />
      {canceledPlan && (
        <SuccessModal
          onClose={handleResetCancelPlan}
          planName={subscriptionName}
        />
      )}
    </Wrapper>
  );
};

export default BillingsList;
